//
// satellite.ts — where a satellite actually is
//
// Translation of `Utils/SatelliteOrbital.swift` as it stands after the
// 2026-08-21 live-data upgrade: Keplerian two-body motion with the dominant
// J2 oblateness corrections, propagated from real CelesTrak element sets.
//
// The honest accuracy statement, same as the Swift: for low Earth orbit
// this lands within roughly 10-30 km of truth over a few days from epoch —
// indistinguishable at global map scale. It is *not* SGP4; drag, higher
// harmonics and lunisolar perturbation are all absent. `propagate` is the
// one function to swap if that ever matters.
//
// Objects with no elements (the offline fallback catalogue, NEOs) keep the
// original Fuser model, which invents a circular orbit seeded off the NORAD
// ID. That model is fiction, and is retained only so the map is never empty.
//

import type { OrbitalElements, TrackedObject } from "../types";

/** Earth's gravitational parameter, km^3/s^2. */
const MU = 398600.4418;
/** Equatorial radius, km (WGS-84). */
const EARTH_RADIUS_KM = 6378.137;
/** Second zonal harmonic — the equatorial bulge. */
const J2 = 1.08262668e-3;
/** Earth's rotation rate, rad/s. */
const EARTH_ROTATION_RATE = 7.292115e-5;

const deg2rad = (d: number) => (d * Math.PI) / 180;
const rad2deg = (r: number) => (r * 180) / Math.PI;

function normalizeLongitude(lon: number): number {
  let l = lon % 360;
  while (l > 180) l -= 360;
  while (l < -180) l += 360;
  return l;
}

export interface SatPosition {
  lat: number;
  lon: number;
  altitudeKm: number;
}

/** Orbital period in minutes, from mean motion (revs/day). */
export function periodMinutes(e: OrbitalElements): number {
  return e.meanMotion > 0 ? 1440 / e.meanMotion : 95;
}

/** Semi-major axis in km, from Kepler's third law. */
export function semiMajorAxisKm(e: OrbitalElements): number {
  const n = (e.meanMotion * 2 * Math.PI) / 86400; // rad/s
  if (n <= 0) return 6928;
  return Math.cbrt(MU / (n * n));
}

export function altitudeKm(e: OrbitalElements): number {
  return semiMajorAxisKm(e) - EARTH_RADIUS_KM;
}

export function velocityKms(e: OrbitalElements): number {
  const a = semiMajorAxisKm(e);
  return a > 0 ? Math.sqrt(MU / a) : 7.6;
}

/**
 * Kepler's equation `M = E - e·sin E` has no closed form for E, so it is
 * solved numerically. Newton-Raphson converges in a handful of iterations
 * for the near-circular orbits this app shows.
 */
function solveKepler(M: number, e: number, tolerance = 1e-8): number {
  let E = e < 0.8 ? M : Math.PI;
  for (let i = 0; i < 12; i++) {
    const delta = (E - e * Math.sin(E) - M) / (1 - e * Math.cos(E));
    E -= delta;
    if (Math.abs(delta) < tolerance) break;
  }
  return E;
}

/** Greenwich Mean Sidereal Time in radians — how far Earth has turned. */
function gmstRadians(dateMs: number): number {
  const jd = dateMs / 86400000 + 2440587.5;
  const n = jd - 2451545.0;
  let gmstHours = (18.697374558 + 24.06570982441908 * n) % 24;
  if (gmstHours < 0) gmstHours += 24;
  return (gmstHours * 15 * Math.PI) / 180;
}

/**
 * Propagates a real element set to an arbitrary time.
 *
 * In plain terms: work out how far around its orbit the satellite has
 * travelled since the elements were issued; correct for the slow drift the
 * Earth's bulge imposes on the orbit itself; work out where that puts it in
 * space; then rotate into the frame that spins with the Earth — which is
 * what a latitude and longitude are.
 */
export function propagate(e: OrbitalElements, dateMs: number): SatPosition {
  const dtMinutes = (dateMs - e.epochMs) / 60000;

  const n0 = (e.meanMotion * 2 * Math.PI) / 1440; // rad/min
  const a = Math.cbrt(MU / Math.pow(n0 / 60, 2)); // km
  const ecc = e.eccentricity;
  const inc = deg2rad(e.inclination);

  // --- J2 secular drift ---------------------------------------------------
  // The bulge drags the orbital plane around the pole (nodal regression) and
  // rotates the ellipse within its own plane (apsidal precession). Over days
  // these dominate everything else.
  const p = a * (1 - ecc * ecc);
  if (!(p > 0) || !(a > 0)) {
    return { lat: 0, lon: 0, altitudeKm: altitudeKm(e) };
  }
  const factor = 1.5 * J2 * Math.pow(EARTH_RADIUS_KM / p, 2) * n0;
  const raanDot = -factor * Math.cos(inc);
  const argPDot = factor * (2 - 2.5 * Math.pow(Math.sin(inc), 2));

  const raan = deg2rad(e.raan) + raanDot * dtMinutes;
  const argP = deg2rad(e.argOfPericenter) + argPDot * dtMinutes;

  // --- Position in the orbital plane -------------------------------------
  const meanAnomaly = deg2rad(e.meanAnomaly) + n0 * dtMinutes;
  const eccAnomaly = solveKepler(meanAnomaly, ecc);

  const trueAnomaly = Math.atan2(
    Math.sqrt(1 - ecc * ecc) * Math.sin(eccAnomaly),
    Math.cos(eccAnomaly) - ecc,
  );
  const r = a * (1 - ecc * Math.cos(eccAnomaly));

  // --- Rotate into Earth-centred inertial coordinates --------------------
  const u = argP + trueAnomaly;
  const xOrbit = r * Math.cos(u);
  const yOrbit = r * Math.sin(u);

  const x = xOrbit * Math.cos(raan) - yOrbit * Math.cos(inc) * Math.sin(raan);
  const y = xOrbit * Math.sin(raan) + yOrbit * Math.cos(inc) * Math.cos(raan);
  const z = yOrbit * Math.sin(inc);

  // --- Inertial to Earth-fixed -------------------------------------------
  const gmst = gmstRadians(dateMs);
  const lonRad = Math.atan2(y, x) - gmst;
  const latRad = Math.asin(Math.max(-1, Math.min(1, z / r)));

  return {
    lat: rad2deg(latRad),
    lon: normalizeLongitude(rad2deg(lonRad)),
    altitudeKm: r - EARTH_RADIUS_KM,
  };
}

/**
 * The original Fuser model — a circular orbit whose phase is seeded from
 * the NORAD ID. Kept only for objects with no real elements.
 */
function legacyPosition(obj: TrackedObject, timeMs: number): SatPosition {
  const periodSec = (obj.orbitalPeriodMin ?? 95) * 60;
  const omega = (2 * Math.PI) / periodSec;

  const timeSec = timeMs / 1000;
  const seed = (obj.noradId ?? 1000) * 1.618;

  const phase = (timeSec * omega + seed) % (2 * Math.PI);
  const incRad = deg2rad(obj.inclinationDeg ?? 53);

  const lat = rad2deg(Math.asin(Math.sin(incRad) * Math.sin(phase)));
  const lonOrbit = Math.atan2(
    Math.cos(incRad) * Math.sin(phase),
    Math.cos(phase),
  );

  const earthRotRad = (timeSec * EARTH_ROTATION_RATE) % (2 * Math.PI);
  const lon = rad2deg(lonOrbit - earthRotRad + (seed % (2 * Math.PI)));

  return {
    lat,
    lon: normalizeLongitude(lon),
    altitudeKm: obj.altitudeKm ?? 550,
  };
}

/**
 * A satellite's position at a given instant — real elements when the object
 * has them, the legacy model when it doesn't, so no caller has to know
 * which kind it is holding. Mirrors `SatelliteOrbital.position(for:atMs:)`.
 */
export function positionOf(obj: TrackedObject, timeMs: number): SatPosition {
  if (obj.elements) return propagate(obj.elements, timeMs);
  return legacyPosition(obj, timeMs);
}

/**
 * How much of an orbit trails behind a satellite. A quarter is enough to
 * read as a direction of travel without wrapping the planet.
 */
const TRAIL_FRACTION = 0.25;

/** Ground-track samples per full orbit. The trail gets its share of these. */
const TRAIL_SAMPLES_PER_ORBIT = 120;

/**
 * The satellite's **wake** — where it has just been, ending at where it is
 * now. Nothing ahead of it.
 *
 * **Rewritten in build 1.5, item 4.** This used to return a full orbit
 * centred on the current instant, so half of every trail was the
 * satellite's own *future* — a line drawn through where it had not been
 * yet, which is not information — and the whole thing was sampled only
 * thirty times, about 12 degrees of arc apart. Combined with the timeline
 * running at 180x, the result redrew somewhere new every frame.
 *
 * Sampling is per unit of arc rather than a fixed count, so a low orbit and
 * a geostationary one come out equally smooth. `traceTrack` subdivides
 * further at draw time; this just has to be dense enough that it can.
 */
export function trajectory(
  obj: TrackedObject,
  currentTimeMs: number,
  fraction = TRAIL_FRACTION,
): Array<{ lat: number; lon: number }> {
  const periodMs = (obj.orbitalPeriodMin ?? 95) * 60 * 1000;
  const span = Math.min(Math.max(fraction, 0), 1);
  const count = Math.max(2, Math.round(TRAIL_SAMPLES_PER_ORBIT * span));
  const stepMs = (periodMs * span) / count;

  const points: Array<{ lat: number; lon: number }> = [];
  for (let i = count; i >= 0; i--) {
    const pos = positionOf(obj, currentTimeMs - i * stepMs);
    points.push({ lat: pos.lat, lon: pos.lon });
  }
  return points;
}
